import * as React from 'react'
import { debounce, ownerDocument, useEnhancedEffect } from '@applift/factor'
import { GridEventListener } from '../../../models/events'
import { ElementSize } from '../../../models'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import {
  useGridApiEventHandler,
  useGridApiOptionHandler,
} from '../../utils/useGridApiEventHandler'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import { GridDimensions, GridDimensionsApi } from './gridDimensionsApi'
import { gridColumnsTotalWidthSelector } from '../columns'
import { useGridSelectorAdvanced } from '../../utils'
import { gridRowsMetaSelector } from '../rows/gridRowsMetaSelector'
import { gridTotalHeaderHeightSelector } from '../columnGrouping/gridColumnGroupsSelector'

const hasScroll = ({
  content,
  container,
  scrollBarSize,
}: {
  content: ElementSize
  container: ElementSize
  scrollBarSize: number
}) => {
  const hasScrollXIfNoYScrollBar = content.width > container.width
  const hasScrollYIfNoXScrollBar = content.height > container.height

  let hasScrollX = false
  let hasScrollY = false
  if (hasScrollXIfNoYScrollBar || hasScrollYIfNoXScrollBar) {
    hasScrollX = hasScrollXIfNoYScrollBar
    hasScrollY =
      content.height + (hasScrollX ? scrollBarSize : 0) > container.height

    // We recalculate the scroll x to consider the size of the y scrollbar.
    if (hasScrollY) {
      hasScrollX = content.width + scrollBarSize > container.width
    }
  }

  return { hasScrollX, hasScrollY }
}

export function useGridDimensions(
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<
    DataGridProcessedProps,
    | 'onResize'
    | 'autoHeight'
    | 'scrollbarSize'
    | 'getRowHeight'
    | 'showColumnGrouping'
    | 'hideColumnHeader'
  >
) {
  const errorShown = React.useRef(false)
  const rootDimensionsRef = React.useRef<ElementSize | null>(null)
  const fullDimensionsRef = React.useRef<GridDimensions | null>(null)
  const rowsMeta = useGridSelectorAdvanced(apiRef, props, gridRowsMetaSelector)
  const totalHeaderHeight = useGridSelectorAdvanced(
    apiRef,
    props,
    gridTotalHeaderHeightSelector
  )
  const updateGridDimensionsRef = React.useCallback(() => {
    const rootElement = apiRef.current.rootElementRef?.current
    const publicApi = apiRef.current.getPublicApi()
    const columnsTotalWidth = gridColumnsTotalWidthSelector(publicApi)

    if (!rootDimensionsRef.current) {
      return
    }

    let scrollBarSize: number
    if (props.scrollbarSize != null) {
      scrollBarSize = props.scrollbarSize
    } else if (!columnsTotalWidth || !rootElement) {
      scrollBarSize = 0
    } else {
      const doc = ownerDocument(rootElement)
      const scrollDiv = doc.createElement('div')
      scrollDiv.style.width = '99px'
      scrollDiv.style.height = '99px'
      scrollDiv.style.position = 'absolute'
      scrollDiv.style.overflow = 'scroll'
      scrollDiv.className = 'scrollDiv'
      rootElement.appendChild(scrollDiv)
      scrollBarSize = scrollDiv.offsetWidth - scrollDiv.clientWidth
      rootElement.removeChild(scrollDiv)
    }

    let viewportOuterSize: ElementSize
    let hasScrollX: boolean
    let hasScrollY: boolean

    if (props.autoHeight) {
      hasScrollY = false
      hasScrollX =
        Math.round(columnsTotalWidth) > rootDimensionsRef.current.width

      viewportOuterSize = {
        width: rootDimensionsRef.current.width,
        height:
          rowsMeta.currentPageTotalHeight + (hasScrollX ? scrollBarSize : 0),
      }
    } else {
      viewportOuterSize = {
        width: rootDimensionsRef.current.width,
        height: rootDimensionsRef.current.height - totalHeaderHeight,
      }

      const scrollInformation = hasScroll({
        content: {
          width: Math.round(columnsTotalWidth),
          height: rowsMeta.currentPageTotalHeight,
        },
        container: {
          width: viewportOuterSize.width,
          height: viewportOuterSize.height,
          // viewportOuterSize.height -
          // pinnedRowsHeight.top -
          // pinnedRowsHeight.bottom,
        },
        scrollBarSize,
      })

      hasScrollY = scrollInformation.hasScrollY
      hasScrollX = scrollInformation.hasScrollX
    }

    const viewportInnerSize: ElementSize = {
      width: viewportOuterSize.width - (hasScrollY ? scrollBarSize : 0),
      height: viewportOuterSize.height - (hasScrollX ? scrollBarSize : 0),
    }

    const newFullDimensions: GridDimensions = {
      viewportOuterSize,
      viewportInnerSize,
      hasScrollX,
      hasScrollY,
      scrollBarSize,
    }

    const prevDimensions = fullDimensionsRef.current
    fullDimensionsRef.current = newFullDimensions

    if (
      newFullDimensions.viewportInnerSize.width !==
        prevDimensions?.viewportInnerSize.width ||
      newFullDimensions.viewportInnerSize.height !==
        prevDimensions?.viewportInnerSize.height
    ) {
      apiRef.current.publishEvent(
        'viewportInnerSizeChange',
        newFullDimensions.viewportInnerSize
      )
    }
  }, [
    apiRef,
    props.scrollbarSize,
    props.autoHeight,
    totalHeaderHeight,
    rowsMeta.currentPageTotalHeight,
  ])

  const resize = React.useCallback<GridDimensionsApi['resize']>(() => {
    updateGridDimensionsRef()
    apiRef.current.publishEvent('debouncedResize', rootDimensionsRef.current!)
  }, [apiRef, updateGridDimensionsRef])

  const getRootDimensions = React.useCallback<
    GridDimensionsApi['getRootDimensions']
  >(() => fullDimensionsRef.current, [])

  const dimensionsApi: GridDimensionsApi = {
    resize,
    getRootDimensions,
    unstable_updateGridDimensionsRef: updateGridDimensionsRef,
  }

  useGridApiMethod(apiRef, dimensionsApi, 'public')

  const debounceResize = React.useMemo<any>(
    () => debounce(resize, 60),
    [resize]
  )

  const isFirstSizing = React.useRef(true)

  const handleResize = React.useCallback<GridEventListener<'resize'>>(
    size => {
      rootDimensionsRef.current = size

      // jsdom has no layout capabilities
      const isJSDOM = /jsdom/.test(window.navigator.userAgent)

      if (
        size.height === 0 &&
        !errorShown.current &&
        !props.autoHeight &&
        !isJSDOM
      ) {
        console.log(
          [
            'The parent DOM element of the data grid has an empty height.',
            'Please make sure that this element has an intrinsic height.',
            'The grid displays with a height of 0px.',
            '',
            //'More details: https://mui.com/r/x-data-grid-no-dimensions.',
          ].join('\n')
        )
        errorShown.current = true
      }
      if (size.width === 0 && !errorShown.current && !isJSDOM) {
        console.log(
          [
            'The parent DOM element of the data grid has an empty width.',
            'Please make sure that this element has an intrinsic width.',
            'The grid displays with a width of 0px.',
            '',
            //'More details: https://mui.com/r/x-data-grid-no-dimensions.',
          ].join('\n')
        )
        errorShown.current = true
      }

      if (isFirstSizing.current) {
        // We want to initialize the grid dimensions as soon as possible to avoid flickering
        resize()
        isFirstSizing.current = false
        return
      }

      debounceResize()
    },
    [props.autoHeight, debounceResize, resize]
  )

  useEnhancedEffect(() => updateGridDimensionsRef(), [updateGridDimensionsRef])
  useGridApiEventHandler(apiRef, 'resize', handleResize)
  useGridApiOptionHandler(apiRef, 'debouncedResize', props.onResize)
}
